import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Green Wheels Scooters';
const SITE_URL = 'https://greenwheelsscooters.ie';
const DEFAULT_DESC = 'Buy electric scooters & electric bikes online in Ireland. Best deals on e scooters, portable electric scooters, long range electric bikes & more. Free delivery from our Dublin store.';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-default.jpg`;

export default function SEO({ title, description, url, image, type = 'website', schema, keywords, children }) {
  const location = useLocation();
  const fullTitle = (title && title.trim())
    ? (title.toLowerCase().includes('green wheels') ? title.trim() : `${title.trim()} - Green Wheels Scooters`)
    : SITE_NAME;
  const desc = (description && description.trim()) ? description.trim() : DEFAULT_DESC;
  const canonical = url ? `${SITE_URL}${url}` : `${SITE_URL}${location.pathname}`;
  const ogImage = image ? (image.startsWith('http') ? image : `${SITE_URL}${image}`) : DEFAULT_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Schema */}
      {schema && (
        Array.isArray(schema)
          ? schema.map((s, i) => <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>)
          : <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
      {children}
    </Helmet>
  );
}

// Helper: Organization schema (use on homepage)
export function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Green Wheels Scooters',
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo.png`,
    description: 'Electric scooter store in Dublin, Ireland. Buy electric scooters and electric bikes online. Best deals on e scooters, Kukirin, Aovopro, Fiido, Engwe. Electric scooter repair and electric bike rentals.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Unit 6, Park Shopping Centre, Prussia Street',
      addressLocality: 'Dublin',
      addressRegion: 'Dublin',
      postalCode: 'D07',
      addressCountry: 'IE'
    },
    telephone: '+353871002241',
    openingHours: 'Mo-Su 10:30-19:00',
    priceRange: '€€',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Electric Scooters & E-Bikes',
      itemListElement: [
        { '@type': 'OfferCatalog', name: 'Electric Scooters' },
        { '@type': 'OfferCatalog', name: 'Electric Bikes' },
        { '@type': 'OfferCatalog', name: 'Electric Scooter Parts' }
      ]
    },
    sameAs: []
  };
}

// Helper: Product schema
export function getProductSchema(product, currency = '€') {
  const currencyCode = currency === '£' ? 'GBP' : 'EUR';
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || `${product.name} - available at Green Wheels Scooters`,
    image: product.images?.length
      ? product.images.map(img => img.startsWith('http') ? img : `${SITE_URL}${img}`)
      : [`${SITE_URL}${product.image || '/images/scooter-1.png'}`],
    brand: { '@type': 'Brand', name: product.brand || 'Green Wheels' },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: currencyCode,
      availability: product.inStock === false
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: 'Green Wheels Scooters' }
    }
  };
}

// Helper: BreadcrumbList schema
export function getBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url ? `${SITE_URL}${item.url}` : undefined
    }))
  };
}
